import { useState } from 'react';
import { requestEyePlugin, type EyePluginDownload } from '../account';

/**
 * The AI EYE plugin, downloadable from a Pro customer's own profile.
 *
 * The link is minted on click rather than rendered into the page: it is
 * single-use and lives twenty minutes, so a URL sitting in the HTML would be
 * either already spent or already expired by the time anyone pressed it. That
 * also keeps it out of browser history and out of any copy of the page.
 */
export default function EyePluginCard({ plan }: { plan: 'free' | 'pro' }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [got, setGot] = useState<EyePluginDownload | null>(null);

  const download = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      const d = await requestEyePlugin();
      setGot(d);
      // Navigating rather than opening a tab: a download does not need one, and
      // a blocked popup would look like a broken button.
      window.location.href = d.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not prepare the download');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-warm-dark-2 rounded-xl p-5 mb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[18rem]">
          <p className="text-text-secondary text-sm leading-relaxed max-w-prose">
            A read-only window onto this data inside your own wp-admin: what AI was asked, whether it
            named you, and which of your pages it read. It never scans your site, never calls an AI, and
            sends nothing about your visitors.
          </p>
          {plan === 'pro' && got && (
            <p className="font-mono text-xs text-text-secondary mt-3">
              v{got.version} · {Math.round(got.size / 1024)} KB · needs WordPress {got.requiresWp}+ and
              PHP {got.requiresPhp}+
              <br />
              <span className="opacity-70">sha256 {got.sha256.slice(0, 16)}…</span>
            </p>
          )}
          {error && <p className="font-mono text-xs text-[#E0574A] mt-3">{error}</p>}
        </div>

        <div className="shrink-0">
          {plan === 'pro' ? (
            <button
              onClick={() => { void download(); }}
              disabled={busy}
              className="bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-40 transition"
            >{busy ? 'Preparing…' : got ? 'Download again' : 'Download the plugin'}</button>
          ) : (
            <a
              href="#pricing"
              className="inline-block border border-cyan/40 text-cyan font-medium rounded-lg px-5 py-2.5 text-sm hover:bg-cyan/10 transition"
            >Part of Pro</a>
          )}
        </div>
      </div>

      {plan === 'pro' && (
        <p className="text-xs text-text-secondary mt-4 pt-4 border-t border-warm-dark-2 leading-relaxed">
          Install it under <span className="font-mono">Plugins → Add New → Upload</span>, then ask me for a
          pairing code for the site you want it to show. After that it updates itself from here, like any
          other plugin.
        </p>
      )}
    </section>
  );
}
